import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { HttpService } from '../../services/http.service';

@Component({
  selector: 'app-export-dropdown',
  templateUrl: './export-dropdown.component.html',
  styleUrls: ['./export-dropdown.component.scss'],
  imports: [CommonModule],
})
export class ExportDropdownComponent {
  @Input() label: string = 'Export as';
  @Input() apiWise: boolean = false;
  @Input() exportApiUrl: string = ''; // New input for API URL
  @Input() tableName: string = 'Table export';
  @Input() currentParams: any = {};
  @Input() isSearch: boolean = false;
  @Output() export = new EventEmitter<'csv' | 'pdf' | 'excel'>();
  @Input() statusColor: string = '#404040';

  @ViewChild('menu') menu!: ElementRef;

  constructor(private httpService: HttpService) {}

  showDropdown = false;
  isProcessing = false;
  isActive: boolean = false;

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.showDropdown = !this.showDropdown;
    this.isActive = this.showDropdown;
    if (this.showDropdown) {
      setTimeout(() => this.positionDropdown(event), 0);
    }
  }

  positionDropdown(event: MouseEvent) {
    const menuEl = this.menu.nativeElement as HTMLElement;
    const menuRect = menuEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const triggerRect = (event.target as HTMLElement)
      .closest('.status-display')!
      .getBoundingClientRect();

    const padding = 8;

    let left = triggerRect.left;
    if (left + menuRect.width > viewportWidth) {
      left = viewportWidth - menuRect.width - padding;
    }
    if (left < padding) {
      left = padding;
    }

    let top = triggerRect.bottom + 8;
    if (top + menuRect.height > viewportHeight) {
      top = triggerRect.top - menuRect.height - 8;
    }

    menuEl.style.left = `${left}px`;
    menuEl.style.top = `${top}px`;
  }

  close(): void {
    this.showDropdown = false;
    this.isActive = false;
  }

  private buildExportUrl(): string {
    if (!this.exportApiUrl) {
      console.error('Export API URL is not provided');
      return '';
    }

    const params = new URLSearchParams();

    // Add common parameters
    if (this.currentParams.type) {
      params.append('type', this.currentParams.type);
    }

    if (this.currentParams.orderby) {
      params.append('orderby', this.currentParams.orderby);
    }

    if (this.currentParams.orderbyname) {
      params.append('orderbyname', this.currentParams.orderbyname);
    }

    if (this.isSearch && this.currentParams.search) {
      params.append('search', this.currentParams.search);
    }

    if (this.currentParams.page) {
      params.append('page', this.currentParams.page);
    }

    params.append('format', 'excel');

    return params.toString()
      ? `${this.exportApiUrl}?${params.toString()}`
      : this.exportApiUrl;
  }

  async onExport(type: 'csv' | 'pdf' | 'excel'): Promise<void> {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    if (this.apiWise) {
      try {
        const exportUrl = this.buildExportUrl();

        if (!exportUrl) {
          console.error('Failed to build export URL');
          this.isProcessing = false;
          this.close();
          return;
        }

        console.log('Export URL:', exportUrl);

        this.httpService.download(exportUrl).subscribe({
          next: (response: HttpResponse<Blob>) => {
            const blob = response.body;

            if (!blob) {
              console.error('Empty response received');
              this.isProcessing = false;
              this.close();
              return;
            }

            const contentDisposition =
              response.headers.get('content-disposition') || '';
            const filename =
              this.extractFilename(contentDisposition) ||
              `${this.tableName.replace(/\s+/g, '_')}_${new Date().getTime()}.xlsx`;

            this.triggerDownload(blob, filename);
            this.export.emit(type);
            this.isProcessing = false;
            this.close();
          },
          error: (error: any) => {
            console.error('Export API error:', error);
            this.isProcessing = false;
            this.close();
          },
        });
      } catch (error) {
        console.error('Export failed:', error);
        this.isProcessing = false;
        this.close();
      }
    } else {
      try {
        const buttonId =
          type === 'csv' ? 'table-upload-csv' : 'table-upload-pdf';
        const button = document.getElementById(
          buttonId,
        ) as HTMLButtonElement | null;
        button?.click();
        this.export.emit(type);
        await new Promise((resolve) => setTimeout(resolve, 500));
      } finally {
        this.isProcessing = false;
        this.close();
      }
    }
  }

  private extractFilename(cd: string): string | null {
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.close();
  }
}
